import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  RelayEnvironmentProvider,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";

import { GiftCardsList } from "./GiftCardsList/GiftCardsList";
import { GiftCardsModalQuery } from "./__generated__/GiftCardsModalQuery.graphql";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setIsSquareModalOpen: (isOpen: boolean) => void;
  onSelectGiftCard?: (giftCardId: string) => void;
  isAddingGiftCard?: boolean;
  appliedGiftCardIds?: string[];
  activeOnly?: boolean;
  showTotalBalance?: boolean;
};

export const GiftCardsModal = ({
  isOpen,
  setIsOpen,
  setIsSquareModalOpen,
  onSelectGiftCard,
  isAddingGiftCard,
  appliedGiftCardIds,
  activeOnly,
  showTotalBalance,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<GiftCardsModalQuery>(
    GiftCardsModalQueryString
  );

  const environment = useRelayEnvironment();

  useEffect(() => {
    if (isOpen) {
      loadQuery(
        { active: activeOnly ? true : undefined },
        { fetchPolicy: "network-only" }
      );
    }
  }, [activeOnly, loadQuery, isOpen]);

  const refetchQuery = useCallback(() => {
    loadQuery(
      { active: activeOnly ? true : undefined },
      { fetchPolicy: "network-only" }
    );
  }, [activeOnly, loadQuery]);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      upperTitle={showTotalBalance ? undefined : t`Gift Cards`}
      hideFooterPadding
      disableContentScroll={true}
      content={
        <RelayEnvironmentProvider environment={environment}>
          <Suspense
            fallback={
              <GiftCardsModalLoadingState showTitle={showTotalBalance} />
            }
          >
            {queryRef ? (
              <GiftCardsModalInner
                queryRef={queryRef}
                refetchQuery={refetchQuery}
                setIsSquareModalOpen={setIsSquareModalOpen}
                setIsGiftCardsModalOpen={setIsOpen}
                onSelectGiftCard={onSelectGiftCard}
                isAddingGiftCard={isAddingGiftCard}
                appliedGiftCardIds={appliedGiftCardIds}
                showTotalBalance={showTotalBalance}
              />
            ) : (
              <GiftCardsModalLoadingState showTitle={showTotalBalance} />
            )}
          </Suspense>
        </RelayEnvironmentProvider>
      }
    />
  );
};

const GiftCardsModalLoadingState = ({ showTitle }: { showTitle?: boolean }) => {
  if (!showTitle) {
    return <Loader />;
  }

  return (
    <Box flex={1} px={6} pb={6}>
      <Text pt={8} pb={6} variant="mainTitle2">
        {t`Gift Cards`}
      </Text>
      <Loader />
    </Box>
  );
};

const GiftCardsModalInner = ({
  refetchQuery,
  queryRef,
  setIsSquareModalOpen,
  setIsGiftCardsModalOpen,
  onSelectGiftCard,
  isAddingGiftCard,
  appliedGiftCardIds,
  showTotalBalance,
}: {
  queryRef: PreloadedQuery<GiftCardsModalQuery>;
  refetchQuery: () => void;
  setIsSquareModalOpen: (isOpen: boolean) => void;
  setIsGiftCardsModalOpen: (isOpen: boolean) => void;
  onSelectGiftCard?: (giftCardId: string) => void;
  isAddingGiftCard?: boolean;
  appliedGiftCardIds?: string[];
  showTotalBalance?: boolean;
}) => {
  const data = usePreloadedQuery(GiftCardsModalQueryString, queryRef);
  const { runAfterIdle } = useModalTransition();

  const openSquareModal = () => {
    setIsGiftCardsModalOpen(false);
    runAfterIdle(() => {
      setIsSquareModalOpen(true);
    });
  };

  return (
    <GiftCardsList
      customerRef={data.session?.currentClientProfile?.member ?? null}
      refetchQuery={refetchQuery}
      openSquareModal={openSquareModal}
      onSelectGiftCard={onSelectGiftCard}
      isAddingGiftCard={isAddingGiftCard}
      appliedGiftCardIds={appliedGiftCardIds}
      showTotalBalance={showTotalBalance}
    />
  );
};

export const GiftCardsModalFragmentString = graphql`
  fragment GiftCardsModal_customer on Customer {
    id
    ...GiftCardsList_customer
  }
`;

export const GiftCardsModalQueryString = graphql`
  query GiftCardsModalQuery($active: Boolean) {
    session {
      currentClientProfile {
        member {
          ... on Customer {
            ...GiftCardsList_customer @arguments(active: $active)
          }
        }
      }
    }
  }
`;
